// Metaphysics API client + types + multipart upload helper.
//
// The console talks directly to Metaphysics for the document-sets feature
// and the refinement (pipelines / suggestions) feature. Metaphysics validates
// Keycloak access tokens against the same realm as cortex (issuer-only — no
// audience check), so the cortex-console JWT is accepted.
//
// Base URL comes from `VITE_METAPHYSICS_API` (workspace-root .env).

import { useMemo } from 'react';

import { useAuth } from '../auth/AuthProvider';

// ── Base URL ──────────────────────────────────────────────────────────────
const env = import.meta.env as Record<string, string | undefined>;
const rawBase = env.VITE_METAPHYSICS_API ?? '';
/** Metaphysics origin, no trailing slash. e.g. `http://localhost:3004`. */
export const metaphysicsBaseUrl = rawBase.replace(/\/$/, '');
/** Metaphysics API root. Metaphysics has no global prefix, so this is the origin. */
export const metaphysicsApiUrl = metaphysicsBaseUrl;

/**
 * Sentinel error thrown when the response is HTML instead of JSON — the
 * giveaway that the request landed on the Vite dev server (port 3001) or a
 * reverse-proxy index.html instead of metaphysics. Almost always means
 * `VITE_METAPHYSICS_API` is unset and a console restart is needed after
 * editing `.env`.
 */
function describeHtmlFallback(method: string, path: string): string {
  const guess = metaphysicsBaseUrl
    ? `Got HTML from ${metaphysicsBaseUrl} — is metaphysics actually running there?`
    : 'VITE_METAPHYSICS_API is unset, so the request hit the Vite dev server. Add `VITE_METAPHYSICS_API=http://localhost:3004` to your .env and restart `pnpm dev` (env vars are baked at startup).';
  return `${method} ${path} returned HTML, not JSON. ${guess}`;
}

// ── Document Set types (mirrors apps/metaphysics/src/services/documentSetService.ts) ──
export type DocumentSetVersionStatus = 'DRAFT' | 'COMMITTED';

export interface IDocumentSet {
  id: string;
  organizationId: string;
  name: string;
  description: string | null;
  /** Per-docset LightRAG role override map. Empty/absent = env defaults. */
  lightragLlmRoleConfig?: {
    extract?: string;
    keyword?: string;
    query?: string;
    vlm?: string;
  };
  /** Per-docset reranker override. Empty/absent = env defaults. */
  lightragRerankConfig?: {
    topN?: number;
    provider?: string;
    model?: string;
  };
  /**
   * The DocumentSetExtractionSchema.id currently governing KG syncs on this
   * doc-set. NULL until the user has generated at least one schema.
   */
  activeExtractionSchemaId?: string | null;
  /** Aggregate counts from the latest committed version (present on list responses). */
  sourceCount?: number;
  artifactCount?: number;
  latestKgVersion?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface IEntryDetail {
  id: string;
  path: string;
  sha256: string;
  sizeBytes: number;
  contentType: string | null;
  kind: 'source' | 'artifact';
  producedBySkillRunId: string | null;
  producedFromEntryIds: string[];
  skillRun: {
    id: string;
    skillId: string;
    modelId: string;
    status: string;
    inputTokens: number | null;
    outputTokens: number | null;
    costUsd: number | null;
    startedAt: string;
    finishedAt: string | null;
  } | null;
  linkedEntries: Array<{
    id: string;
    path: string;
    sha256: string;
    sizeBytes: number;
    contentType: string | null;
    kind: 'source' | 'artifact';
  }>;
}

export type IDocumentSetExtractionSchemaGeneratedFrom = 'ai_generated' | 'user_edited';

export interface IDocumentSetExtractionSchema {
  id: string;
  documentSetId: string;
  organizationId: string;
  version: number;
  domain: string;
  entityTypes: string[];
  domainContext: string;
  isLocked: boolean;
  generatedFrom: IDocumentSetExtractionSchemaGeneratedFrom;
  generationPrompt: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  costUsd: number | string | null;
  createdAt: string;
}

export interface IDocumentSetVersion {
  id: string;
  organizationId: string;
  documentSetId: string;
  /** NULL while DRAFT; populated on commit. */
  name: string | null;
  description: string | null;
  status: DocumentSetVersionStatus;
  parentVersionId: string | null;
  createdBySubject: string;
  createdAt: string;
  committedAt: string | null;
}

export interface IManifestEntry {
  path: string;
  sha256: string;
  sizeBytes: number;
  contentType: string | null;
}

export interface IVersionWithManifest {
  version: IDocumentSetVersion;
  manifest: IManifestEntry[];
}

export interface IVersionDiff {
  fromVersionId: string;
  toVersionId: string;
  added: IManifestEntry[];
  removed: IManifestEntry[];
  modified: Array<{
    path: string;
    before: { sha256: string; sizeBytes: number; contentType: string | null };
    after: { sha256: string; sizeBytes: number; contentType: string | null };
  }>;
}

// ── DocumentSet → KG (direct path) ────────────────────────────────────────────

/**
 * Mirrors apps/metaphysics/db/types.ts DocumentSetKgVersion, decorated
 * with the latest sync-job's state + error from the server's join. The
 * server promotes long-stuck non-terminal jobs to a synthetic `stale`
 * verdict; the UI uses that to render a failed-like badge instead of an
 * indefinite "indexing…" spinner.
 */
export type IDocumentSetKgSyncJobVerdict =
  | 'pending'
  | 'converting'
  | 'pushing'
  | 'indexing'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'stale';

export interface IDocumentSetKgVersion {
  id: string;
  organizationId: string;
  documentSetId: string;
  documentSetVersionId: string;
  version: number;
  kgWorkspace: string;
  domain: string;
  entityTypes: string[];
  entityExamples: Record<string, string[]>;
  domainContext: string;
  knownEntities: Array<{ name: string; type: string; description: string }>;
  manifestInputTokens: number | null;
  manifestOutputTokens: number | null;
  manifestCostUsd: number | null;
  lightragLlmModel: string | null;
  lightragEmbeddingModel: string | null;
  lightragLlmInputTokens: number | null;
  lightragLlmOutputTokens: number | null;
  lightragEmbeddingTokens: number | null;
  lightragCostUsd: number | null;
  createdAt: string;
  /**
   * Job id of the underlying sync job for this KG version, when one
   * exists. Used by the UI to reopen the progress modal on any row —
   * even after the modal has been dismissed — by passing this id to
   * `useDocumentSetKgSyncJob`.
   */
  syncJobId: string | null;
  /** Job-level state from the join; null if no job ever ran for this KG version. */
  syncJobState: IDocumentSetKgSyncJobVerdict | null;
  /** Job-level error message; populated on failed/cancelled outcomes. */
  syncJobError: string | null;
  /** ISO timestamp when the job entered its terminal state, if any. */
  syncJobFinishedAt: string | null;
}

export interface IDocumentSetKgChangelog {
  id: string;
  organizationId: string;
  documentSetId: string;
  documentSetKgVersionId: string;
  kgVersion: number;
  previousKgVersion: number | null;
  newFiles: Array<{ path: string; sha256: string; sizeBytes: number }>;
  changedFiles: Array<{ path: string; fromSha256: string; toSha256: string }>;
  removedFiles: Array<{ path: string; lastSha256: string }>;
  createdAt: string;
}

export interface IDocumentSetKgProvenance {
  id: string;
  organizationId: string;
  documentSetId: string;
  documentSetKgVersionId: string;
  documentSetVersionId: string;
  kgVersion: number;
  kgWorkspace: string;
  outputGraphHash: string | null;
  builtBy: string | null;
  builtAt: string;
}

export type IDocumentSetKgSyncJobState =
  | 'pending'
  | 'converting'
  | 'pushing'
  | 'indexing'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type IDocumentSetKgSyncJobItemState =
  | 'pending'
  | 'pushed'
  | 'extracting'
  | 'completed'
  | 'failed';

/**
 * Per-item conversion-phase state. Independent of the LightRAG-push state
 * above so the UI can express "this item failed conversion (skipping
 * extraction)" vs "this item failed extraction at LightRAG."
 */
export type IDocumentSetKgSyncJobItemConversionState =
  | 'not_needed'
  | 'pending'
  | 'in_progress'
  | 'converted'
  | 'failed'
  | 'unsupported_format';

export interface IDocumentSetKgSyncJob {
  id: string;
  organizationId: string;
  documentSetId: string;
  documentSetKgVersionId: string;
  kgVersion: number;
  kgWorkspace: string;
  mode: 'full' | 'delta';
  state: IDocumentSetKgSyncJobState;
  totalItems: number;
  pushedItems: number;
  completedItems: number;
  failedItems: number;
  /** Items that successfully converted (sandbox-produced markdown). */
  convertedItems: number;
  /** Items terminal-failed at classification time (e.g. .dwg) — never converted, never pushed. */
  unsupportedItems: number;
  /** Files filtered out before becoming job items (zone-id sidecars, unknown extensions). */
  skippedItems: number;
  attempt: number;
  lastError: string | null;
  startedAt: string;
  lastProgressAt: string;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IDocumentSetKgSyncJobItem {
  id: string;
  organizationId: string;
  jobId: string;
  documentSetVersionId: string;
  path: string;
  sha256: string;
  state: IDocumentSetKgSyncJobItemState;
  lightragDocId: string | null;
  attempt: number;
  lastError: string | null;
  pushedAt: string | null;
  completedAt: string | null;
  /**
   * Whether this item's source bytes need to be materialized to markdown
   * via convert.py before LightRAG push. Derived from the file extension
   * at job-creation time.
   */
  requiresConversion: boolean;
  conversionState: IDocumentSetKgSyncJobItemConversionState;
  conversionAttempt: number;
  converterTool: string | null;
  conversionError: string | null;
  convertedCharCount: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface IDocumentSetKgSyncJobWithItems {
  job: IDocumentSetKgSyncJob;
  items: IDocumentSetKgSyncJobItem[];
}

export interface IGenerateKgExtractionInput {
  entityTypes: string[];
  domain?: string;
  domainContext?: string;
  knownEntities?: string[];
  entityExamples?: Record<string, string[]>;
}

export interface IGenerateKgRequest {
  mode?: 'delta' | 'full';
  /**
   * Optional as of the DocumentSetExtractionSchema feature (migration
   * 1816…). When omitted, the backend uses the doc-set's active
   * extraction schema. When both are absent, the backend returns
   * `INVALID_STATE`.
   */
  extraction?: IGenerateKgExtractionInput;
}

export interface IGenerateKgResponse {
  kgVersion: number;
  kgWorkspace: string;
  documentSetKgVersionId: string;
  jobId: string;
}

// ── Lineage ───────────────────────────────────────────────────────────────────

export interface ILineageArtifact {
  id: string;
  path: string;
  sha256: string;
  sizeBytes: number;
  contentType: string | null;
  skillRunId: string | null;
  skillId: string | null;
  /**
   * `conversion` artifacts are the KG build's markdown output, projected in
   * from the conversion cache — they have no entry row, so `id` is a
   * synthetic `converted:…` handle and the inspector cannot fetch detail.
   */
  origin: 'skill-run' | 'conversion';
  converterTool: string | null;
}

export interface ILineageEntry {
  id: string;
  path: string;
  sha256: string;
  sizeBytes: number;
  contentType: string | null;
  artifacts: ILineageArtifact[];
}

export interface ILineageResponse {
  versionId: string | null;
  sources: ILineageEntry[];
}

// ── Skill runs ────────────────────────────────────────────────────────────────

export type ISkillRunStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface ISkillRun {
  id: string;
  skillId: string;
  modelId: string;
  status: ISkillRunStatus;
  totalItems: number;
  completedItems: number;
  failedItems: number;
  inputTokens: number | null;
  outputTokens: number | null;
  costUsd: number | null;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
}

// ── Refinement (pipeline / suggestions) types ─────────────────────────────
export interface IPipeline {
  id: string;
  name: string;
  /** Other fields exist on the server response but the console only needs name. */
}

export interface IPipelinesResponse {
  items: IPipeline[];
}

/** Progress event streamed from `GET /runs/:runId/progress` (SSE). */
export interface IRunProgressEvent {
  progressPercent?: number;
  currentStage?: string;
  stageMessage?: string;
  status?: 'pending' | 'running' | 'completed' | 'failed';
}

/**
 * Mirrors `SuggestionEventResponse` in
 * `apps/metaphysics/src/validation/schemas.ts`. One row in the
 * `/suggestions` feed: a new or changed artifact whose blueprint target was
 * marked `isSuggestion = true`.
 */
export interface ISuggestionEvent {
  pipelineId: string;
  pipelineName: string;
  kgVersion: number;
  /** ISO 8601 timestamp (server formats `Date → string` before sending). */
  changedAt: string;
  kind: 'new' | 'changed';
  artifactId: string;
  artifactName: string;
  /** 'markdown' | 'csv' | 'mermaid' (string at the wire level). */
  artifactType: string;
  version: number;
  /** Only present when `kind === 'changed'`. */
  fromVersion?: number;
  isSuggestional: true;
}

// ── Blueprint types (mirrors apps/metaphysics blueprintResponseSchema) ────
export type IArtifactType = 'markdown' | 'csv' | 'mermaid';

export interface IPromptStep {
  name: string;
  content: string;
}

export interface ITargetArtifact {
  name: string;
  artifactType: IArtifactType;
  description?: string | null;
  /** Marks this artifact as suggestional material exposed externally. */
  isSuggestion?: boolean;
}

export interface IBlueprint {
  id: string;
  name: string;
  /** Legacy single prompt — newer blueprints use `prompts`. */
  prompt?: string | null;
  prompts?: IPromptStep[];
  skillsRepoUrl?: string | null;
  targetArtifacts?: ITargetArtifact[];
  createdAt?: string;
  updatedAt?: string;
}

export interface IBlueprintCreateInput {
  name: string;
  prompts: IPromptStep[];
  skillsRepoUrl?: string | null;
  targetArtifacts: ITargetArtifact[];
}

export type IBlueprintUpdateInput = IBlueprintCreateInput;

// ── HTTP wrapper ───────────────────────────────────────────────────────────
type JsonInit = { json?: unknown };

interface IResponseLike {
  json: <T>() => Promise<T>;
  text: () => Promise<string>;
}

export interface IUploadProgress {
  loaded: number;
  total: number;
}

export interface IUploadFileOptions {
  setId: string;
  draftId: string;
  /** POSIX-style virtual path inside the set (e.g. `manuals/feed-tank.md`). */
  path: string;
  file: File | Blob;
  onProgress?: (p: IUploadProgress) => void;
  signal?: AbortSignal;
}

export interface IMetaphysicsClient {
  // Generic HTTP — used by the document-sets feature.
  get: (path: string) => IResponseLike;
  post: (path: string, init?: JsonInit) => IResponseLike;
  patch: (path: string, init?: JsonInit) => IResponseLike;
  delete: (path: string) => Promise<void>;
  /**
   * Multipart upload of a single file to a draft version. Uses XMLHttpRequest
   * because `fetch` has no upload-progress events in any browser. Resolves
   * with the parsed JSON entry on 2xx; rejects on network error / non-2xx.
   */
  uploadFile: (opts: IUploadFileOptions) => Promise<IManifestEntry>;

  // Refinement convenience methods.
  listPipelines: () => Promise<IPipeline[]>;
  /** Trigger a pipeline run. Returns the new runId on success. */
  runPipeline: (pipelineConfigId: string) => Promise<{ runId: string }>;
  /** Build the SSE URL for a run, including the bearer token in the query string. */
  progressUrl: (runId: string) => string;
  /**
   * Open an SSE connection to stream run progress. Resolves when the run hits
   * a terminal state (completed / failed) or the connection drops.
   */
  streamProgress: (runId: string, onEvent: (evt: IRunProgressEvent) => void) => Promise<void>;
  /** List org-wide suggestion events, newest first. */
  listSuggestions: (limit?: number) => Promise<ISuggestionEvent[]>;
  /**
   * Fetch the raw text body of an artifact (markdown / csv / mermaid). The
   * server returns `text/markdown|csv|plain`, NOT JSON.
   */
  getArtifactContent: (pipelineId: string, artifactId: string) => Promise<string>;

  /**
   * Fetch the converted-artifact markdown for a committed file (markitdown for
   * PDFs/Office, `claude-vision-caption` for images). Empty string when the
   * file was ingested as-is (server returns 204).
   */
  getConvertedContent: (setId: string, path: string) => Promise<string>;
  /** Presigned URL of a committed file's original bytes, for inline `<img>`/`<iframe>` display. */
  getFileUrl: (setId: string, path: string) => Promise<string>;
  /** Presigned URL of an extracted figure crop by sha256, for inline `<img>` in the converted view. */
  getMediaUrl: (setId: string, sha256: string) => Promise<string>;

  // Blueprint convenience methods (admin / superuser only on the UI side;
  // server enforces normal org scoping).
  listBlueprints: () => Promise<IBlueprint[]>;
  getBlueprint: (id: string) => Promise<IBlueprint>;
  createBlueprint: (input: IBlueprintCreateInput) => Promise<IBlueprint>;
  updateBlueprint: (id: string, input: IBlueprintUpdateInput) => Promise<IBlueprint>;
  deleteBlueprint: (id: string) => Promise<void>;
}

function makeClient(token: string | null): IMetaphysicsClient {
  function call(method: string, path: string, init?: JsonInit): IResponseLike {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (init?.json !== undefined) headers['Content-Type'] = 'application/json';
    const promise = fetch(`${metaphysicsBaseUrl}/${path}`, {
      method,
      headers,
      body: init?.json !== undefined ? JSON.stringify(init.json) : undefined,
    }).then(async (res) => {
      if (!res.ok) {
        let body = '';
        try {
          body = await res.text();
        } catch {
          /* ignore */
        }
        throw new Error(`${method} ${path} → ${res.status}${body ? `: ${body}` : ''}`);
      }
      return res;
    });
    return {
      json: <T>(): Promise<T> =>
        promise.then(async (r) => {
          const ct = r.headers.get('content-type') ?? '';
          if (!ct.includes('json')) {
            throw new Error(describeHtmlFallback(method, path));
          }
          return r.json() as Promise<T>;
        }),
      text: () => promise.then((r) => r.text()),
    };
  }

  function uploadFile(opts: IUploadFileOptions): Promise<IManifestEntry> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const url = `${metaphysicsBaseUrl}/document-sets/${encodeURIComponent(opts.setId)}/drafts/${encodeURIComponent(opts.draftId)}/files`;
      xhr.open('POST', url);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      // Don't set Content-Type — the browser fills in the multipart boundary.
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && opts.onProgress) {
          opts.onProgress({ loaded: e.loaded, total: e.total });
        }
      });
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText) as IManifestEntry);
          } catch (err) {
            reject(new Error(`upload: malformed JSON response (${(err as Error).message})`));
          }
        } else {
          reject(new Error(`upload ${opts.path} → ${xhr.status}: ${xhr.responseText || ''}`));
        }
      });
      xhr.addEventListener('error', () => reject(new Error(`upload ${opts.path} → network error`)));
      xhr.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')));
      if (opts.signal) {
        if (opts.signal.aborted) {
          xhr.abort();
          return;
        }
        opts.signal.addEventListener('abort', () => xhr.abort(), { once: true });
      }
      const form = new FormData();
      form.append('path', opts.path);
      // Preserve filename when available so server-side defaults are sensible.
      const filename = opts.file instanceof File ? opts.file.name : opts.path.split('/').pop();
      form.append('file', opts.file, filename);
      xhr.send(form);
    });
  }

  function progressUrl(runId: string): string {
    // EventSource can't set Authorization headers, so the metaphysics SSE
    // endpoint accepts the token as a query param (same pattern as the
    // metaphysics frontend — see runs.progressUrl in apps/metaphysics).
    const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
    return `${metaphysicsApiUrl}/runs/${runId}/progress${tokenParam}`;
  }

  return {
    get: (path) => call('GET', path),
    post: (path, init) => call('POST', path, init),
    patch: (path, init) => call('PATCH', path, init),
    delete: async (path) => {
      await call('DELETE', path).text();
    },
    uploadFile,

    listPipelines: async () => {
      const data = await call('GET', 'pipeline').json<IPipelinesResponse>();
      return data.items;
    },
    runPipeline: async (pipelineConfigId) => {
      const data = await call('POST', 'run', {
        json: { pipelineConfigId },
      }).json<{ success: boolean; runId?: string; error?: string }>();
      if (!data.success || !data.runId) {
        throw new Error(data.error ?? 'Pipeline run failed to start');
      }
      return { runId: data.runId };
    },
    progressUrl,
    streamProgress: (runId, onEvent) =>
      new Promise<void>((resolve) => {
        const src = new EventSource(progressUrl(runId));
        src.onmessage = (e) => {
          try {
            const evt = JSON.parse(e.data) as IRunProgressEvent;
            onEvent(evt);
            if (evt.status === 'completed' || evt.status === 'failed') {
              src.close();
              resolve();
            }
          } catch (err) {
            // Bad SSE frame — keep the connection alive but log for debugging.
            console.error('metaphysics SSE parse error', err);
          }
        };
        src.onerror = () => {
          src.close();
          resolve();
        };
      }),
    listSuggestions: async (limit) => {
      const qs = limit != null ? `?limit=${encodeURIComponent(String(limit))}` : '';
      const data = await call('GET', `suggestions${qs}`).json<{ items: ISuggestionEvent[] }>();
      return data.items;
    },
    getArtifactContent: (pipelineId, artifactId) =>
      call('GET', `pipeline/${pipelineId}/artifacts/${artifactId}/content`).text(),
    getConvertedContent: (setId, path) =>
      call(
        'GET',
        `document-sets/${encodeURIComponent(setId)}/versions/latest/converted/${encodeSplatPath(path)}`,
      ).text(),
    getFileUrl: (setId, path) =>
      call(
        'GET',
        `document-sets/${encodeURIComponent(setId)}/versions/latest/file-url/${encodeSplatPath(path)}`,
      )
        .json<{ url: string }>()
        .then((r) => r.url),
    getMediaUrl: (setId, sha256) =>
      call(
        'GET',
        `document-sets/${encodeURIComponent(setId)}/media/${encodeURIComponent(sha256)}/url`,
      )
        .json<{ url: string }>()
        .then((r) => r.url),

    listBlueprints: async () => {
      const data = await call('GET', 'blueprint').json<{ items: IBlueprint[] }>();
      return data.items;
    },
    getBlueprint: (id) => call('GET', `blueprint/${encodeURIComponent(id)}`).json<IBlueprint>(),
    createBlueprint: (input) => call('POST', 'blueprint', { json: input }).json<IBlueprint>(),
    updateBlueprint: (id, input) =>
      call('PUT', `blueprint/${encodeURIComponent(id)}`, { json: input }).json<IBlueprint>(),
    deleteBlueprint: async (id) => {
      await call('DELETE', `blueprint/${encodeURIComponent(id)}`).text();
    },
  };
}

/**
 * Returns a Metaphysics HTTP client bound to the current Keycloak access
 * token. Memoized on token so silent-renew flows transparently hand a fresh
 * JWT to the next call without re-rendering consumers.
 */
export function useMetaphysics(): IMetaphysicsClient {
  const { token } = useAuth();
  return useMemo(() => makeClient(token), [token]);
}

// ── Skill-run cost estimation ─────────────────────────────────────────────

export interface ISkillCostEstimate {
  skillId: string;
  contentItemCount: number;
  modelId: string;
  estimatedInputTokens: number;
  estimatedOutputTokens: number;
  estimatedCostUsd: number;
  disclaimer: string;
}

export async function estimateSkillCost(
  token: string | null,
  skillId: string,
  count: number,
  modelId = 'claude-sonnet-4-6',
): Promise<ISkillCostEstimate> {
  const contentItemIds = Array.from({ length: count }, () => crypto.randomUUID());
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${metaphysicsBaseUrl}/v1/skill-runs/cost-estimate`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ skillId, contentItemIds, modelId }),
  });
  if (!res.ok) throw new Error(`cost-estimate ${res.status}`);
  return res.json() as Promise<ISkillCostEstimate>;
}

export async function enqueueSkillRun(
  token: string | null,
  skillId: string,
  documentSetId: string,
  opts?: { skillKind?: string; skillBody?: string },
): Promise<{ jobId: string | undefined }> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${metaphysicsBaseUrl}/v1/skill-runs/enqueue`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ skillId, documentSetId, ...opts }),
  });
  if (!res.ok) throw new Error(`skill-run enqueue ${res.status}`);
  return res.json() as Promise<{ jobId: string | undefined }>;
}

export interface IRagAnswer {
  answer: string;
  references?: Array<{ title: string; url?: string }>;
}

export async function queryRag(
  token: string | null,
  documentSetId: string,
  query: string,
  mode: 'mix' | 'hybrid' | 'local' | 'global' | 'naive' = 'mix',
): Promise<IRagAnswer> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${metaphysicsBaseUrl}/rag/query`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ query, mode, documentSetId }),
  });
  if (!res.ok) throw new Error(`rag/query ${res.status}`);
  const data = (await res.json()) as { response?: string; answer?: string };
  return { answer: data.response ?? data.answer ?? '' };
}

export async function getEntryDetail(
  token: string | null,
  documentSetId: string,
  entryId: string,
): Promise<IEntryDetail> {
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(
    `${metaphysicsBaseUrl}/document-sets/${encodeURIComponent(documentSetId)}/versions/latest/entries/${encodeURIComponent(entryId)}`,
    { headers },
  );
  if (!res.ok) throw new Error(`entry-detail ${res.status}`);
  return res.json() as Promise<IEntryDetail>;
}

// ── Path encoding helpers ─────────────────────────────────────────────────
/**
 * Encode a virtual path for the splat segment in DELETE / GET file routes:
 * each `/`-separated segment is URI-encoded but the slashes themselves are
 * preserved (NestJS path-to-regexp@8 expects the unescaped slashes).
 */
export function encodeSplatPath(path: string): string {
  return path.split('/').map(encodeURIComponent).join('/');
}

/** URL of a file in a specific committed version (server 302s to MinIO). */
export function fileUrl(setId: string, versionName: string, path: string): string {
  return `${metaphysicsBaseUrl}/document-sets/${encodeURIComponent(setId)}/versions/${encodeURIComponent(versionName)}/files/${encodeSplatPath(path)}`;
}

/** URL of a file in the latest committed version. */
export function latestFileUrl(setId: string, path: string): string {
  return `${metaphysicsBaseUrl}/document-sets/${encodeURIComponent(setId)}/versions/latest/files/${encodeSplatPath(path)}`;
}
